import React, { useMemo } from 'react';
import { batchActions } from 'redux-batched-actions';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type MenuItemSearch,
	type MenuNode,
	type MenuItem,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useTextFilter } from '@atlassian/jira-software-context-menu/src/common/utils/use-text-filter/index.tsx';
import { useContextStartAnalytics } from '../../../../../../common/hooks/use-context-start-analytics/index.tsx';
import type { IssueParent } from '../../../../../../model/issue-parent/types.tsx';
import type { CardId } from '../../../../../../model/types.tsx';
import { cardParentSetOptimistic } from '../../../../../../state/actions/parent/index.tsx';
import { useBacklogActionCreator, useBacklogSelector } from '../../../../../../state/index.tsx';
import { getCards, makeCardKeyFromCardId } from '../../../../../../state/selectors/cards/index.tsx';
import { PARENT_MENU_ID, PARENT_SEARCH_MENU_ID } from '../../constants.tsx';
import messages from '../../messages.tsx';
import { useIssueParents } from './use-issue-parents/index.tsx';

export const MAX_ITEMS_BEFORE_SEARCH_ENABLED = 6;

type UseParentContentProps = {
	issueParents: IssueParent[];
	loading: boolean;
	cardSelectionValid: boolean;
	showUnlinkMenuItem: boolean;
	onUpdateParent: (parent: IssueParent | null, analyticsEvent: UIAnalyticsEvent) => void;
	selectedCardIds?: CardId[];
};

const UNLINK = 'unlink' as const;

type UnlinkMenuOptionType = {
	type: typeof UNLINK;
	label: string;
};

type MenuOption = UnlinkMenuOptionType | IssueParent;

const isUnlinkMenuOption = (option: MenuOption): option is UnlinkMenuOptionType =>
	'type' in option && option.type === UNLINK;

const transformOptionToString = (option: MenuOption) => {
	if (isUnlinkMenuOption(option)) {
		return option.label;
	}
	return `${option.key} ${option.summary}`;
};

export const useParentContent = ({
	issueParents,
	loading,
	cardSelectionValid,
	showUnlinkMenuItem,
	onUpdateParent,
	selectedCardIds,
}: UseParentContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();
	const menuOptions = useMemo<MenuOption[]>(
		() => [
			...(showUnlinkMenuItem
				? [{ type: UNLINK, label: formatMessage(messages.parentUnlinkOptionLabel) }]
				: []),
			...issueParents,
		],
		[formatMessage, issueParents, showUnlinkMenuItem],
	);
	const [filteredMenuOptions, updateFilter] = useTextFilter<MenuOption>(
		menuOptions,
		transformOptionToString,
	);
	const onMouseEnter = useContextStartAnalytics({
		selectedCardIds: selectedCardIds ?? [],
		fieldType: PARENT_TYPE,
		fieldKey: PARENT_TYPE,
	});

	return useMemo(() => {
		if (!cardSelectionValid || (!issueParents.length && !showUnlinkMenuItem)) {
			return [];
		}

		const options: MenuItem[] = filteredMenuOptions.map((option) =>
			isUnlinkMenuOption(option)
				? {
						type: MenuNodeType.Item,
						id: `${PARENT_MENU_ID}/value__unlink`,
						label: option.label,
						onClick: (analyticsEvent) => {
							onUpdateParent(null, analyticsEvent);
						},
					}
				: {
						type: MenuNodeType.Item,
						id: `${PARENT_MENU_ID}/value__${option.id}`,
						label: transformOptionToString(option),
						icon: (
							<img
								width={token('space.200', '16px')}
								height={token('space.200', '16px')}
								src={option.issueType.iconUrl}
								alt=""
							/>
						),
						onClick: (analyticsEvent) => {
							onUpdateParent(option, analyticsEvent);
						},
					},
		);

		const showSearch =
			issueParents.length + (showUnlinkMenuItem ? 1 : 0) > MAX_ITEMS_BEFORE_SEARCH_ENABLED;

		const subMenu: MenuNode[] = showSearch
			? [
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					{
						type: MenuNodeType.Search,
						id: PARENT_SEARCH_MENU_ID,
						children: options,
						hasSearch: true,
						// Parents are only fetched async once during mount
						isAsyncSearch: false,
						isLoading: loading,
						onChange: updateFilter,
						resultsCount: filteredMenuOptions.length,
					} as MenuItemSearch,
				]
			: options;

		return [
			{
				type: MenuNodeType.Item,
				id: PARENT_MENU_ID,
				label: formatMessage(messages.parentOptionLabel),
				submenuWidth: 284,
				children: subMenu,
				...(fg('one_event_rules_them_all_fg') && {
					onMouseEnter,
				}),
			},
		];
	}, [
		cardSelectionValid,
		issueParents.length,
		showUnlinkMenuItem,
		filteredMenuOptions,
		loading,
		updateFilter,
		formatMessage,
		onMouseEnter,
		onUpdateParent,
	]);
};

export const useParentEndAnalytics = (oldParentId: number | null) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (newValue: number | null | undefined) => {
		getUpdateAnalyticsFlowHelper().setActionTakenAttributes(
			PARENT_TYPE,
			oldParentId,
			newValue ?? null,
		);
		getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(PARENT_TYPE, {
			analytics: createAnalyticsEvent({}),
			attributes: {
				fieldType: PARENT_TYPE,
			},
		});
	};
};

export const useParent = (selectedCardIds: CardId[]): MenuNode[] => {
	const getIssueKey = useBacklogSelector(makeCardKeyFromCardId);
	const allCards = useBacklogSelector(getCards);
	const selectedCards = useMemo(
		() => selectedCardIds.map((cardId) => allCards[cardId]).filter(Boolean),
		[allCards, selectedCardIds],
	);

	const firstCardParentId = selectedCards.length ? selectedCards[0].parentId : null;
	const fireAnalyticsEnd = fg('one_event_rules_them_all_fg')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useParentEndAnalytics(firstCardParentId)
		: noop;

	const {
		issueParents: allIssueParents,
		loading,
		cardSelectionValid,
	} = useIssueParents(selectedCards);

	const multipleCardsSelected = selectedCardIds.length > 1;
	const showUnlinkMenuItem = selectedCards.some((card) => card.parentId !== null);

	// If we're only editing a single card, remove any
	// epic from the list that's assigned to this card
	const issueParents = useMemo(
		() =>
			!multipleCardsSelected && firstCardParentId
				? allIssueParents.filter((parent) => parent.id !== String(firstCardParentId))
				: allIssueParents,
		[allIssueParents, firstCardParentId, multipleCardsSelected],
	);

	const onUpdateParent = useBacklogActionCreator(
		(parent: IssueParent | null, analyticsEvent: UIAnalyticsEvent) => {
			if (selectedCardIds.length === 1 && fg('one_event_rules_them_all_fg')) {
				fireAnalyticsEnd(parent?.id);
			}
			return batchActions(
				selectedCardIds.map((id) =>
					cardParentSetOptimistic(
						{
							cardId: id,
							parent,
						},
						getIssueKey(id),
						analyticsEvent,
					),
				),
			);
		},
	);

	return useParentContent({
		issueParents,
		onUpdateParent,
		showUnlinkMenuItem,
		loading,
		cardSelectionValid,
		...(fg('one_event_rules_them_all_fg') && { selectedCardIds }),
	});
};
